import { FormRegistrationData, getValidationSchema } from './validationSchemas';
import { ChangeEvent, Dispatch, FormEvent, Fragment, SetStateAction, useEffect, useState } from 'react';
import { formatMessage } from 'i18n/CurrentLocale';
import { FormattedMessage, useIntl } from 'react-intl';
import { CountryCode, SupportedLocale } from 'types';
import { ValidationError } from './ValidationError';
import { ModalEnum, RegistrationFlowType } from 'myParkdUtils/enums';
import { useDebounce, useSkipFirstEffect } from 'myParkdUtils/customHooks';
import { isValidEmailAddress } from 'myParkdUtils/validationExpressions';
import { mapUserLanguageToCountry, TouchedFields, TouchedFieldsAccount } from './utils';
import { FormInvalidMessage } from './FormInvalidMessage';
import TelematicProvidersDropdown from 'screens/registration/InputTelematicProviders';
import PhoneInputWithCountrySelect, { isValidPhoneNumber } from 'react-phone-number-input';
import { getPhoneCountryLabels } from 'myParkdUtils/phoneLocales';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { PasswordInput } from './InputPassword';
import { InputAcceptTC } from './InputAcceptTC';
import { DropdownOption } from 'screens/modals/ChooseConnectionModal';
import { Api } from 'api';
import { Schema } from 'yup';
import ClickableInfoIcon from 'components/ClickableInfoIcon';
import ReactFlagsSelect from 'react-flags-select';
import placeholders from 'string-translations/placeholders';
import { getCountryCodeLabels, stripStringFromNonAlphaNumericCharacters } from 'myParkdUtils/utils';
import { countryCodes } from './RegistrationScreen';
import { GAAction, trackDataLayerEvent } from 'myParkdUtils/googleAnalytics';


const initialTouchedFields: TouchedFieldsAccount = {
    // user
    firstName: false,
    lastName: false,
    phoneNumber: false,
    emailAddress: false,
    password: false,
    // fleet
    numCars: false,
    telematicProvider: false,
    otherTelematicProvider: false,
    acceptedTC: false,
    // oem
    vin: false,
    licensePlate: false,
    licensePlateCountryCode: false
}

export const StepAccountDetails = (props: Props) => {
    const intl = useIntl();

    const { flowType, formData, userLanguage, touched, errors, setTouched, setErrors,
        handleUserInput, setFormData, setFormStep, handleBlur, getClassNames, validateForm } = props;

    const [validPhone, setValidPhone] = useState<boolean>(true);
    const [emailExists, setEmailExists] = useState<boolean>(false);
    const [loadingEmail, setLoadingEmail] = useState<boolean>(false);
    const [showValidation, setShowValidation] = useState<boolean>(false);

    const debouncedEmail = useDebounce(formData.emailAddress, 500);

    const validationSchema = getValidationSchema(intl, 'STEP_ACCOUNT');

    useEffect(() => {
        (async () => {
            const { errors } = await validateForm(validationSchema, { validPhone, emailExists });
            setErrors(errors);
        })();
    }, [formData, validPhone, emailExists])

    useSkipFirstEffect(() => {
        (async () => {
            try {
                if (isValidEmailAddress(debouncedEmail)) {
                    setLoadingEmail(true);
                    const exists = await Api.checkIfEmailExists(debouncedEmail);
                    setEmailExists(exists);
                }
            } catch (e) {
                console.error('failed to validate if emailaddress exists...', e);
            } finally {
                setLoadingEmail(false);
            }
        })()
    }, [debouncedEmail]);


    const onPhoneNumberChange = (phoneNumber: string) => {
        setFormData((prevState) => ({ ...prevState, phoneNumber }));
        setTouched((prevState) => ({ ...prevState, phoneNumber: true }));
        setValidPhone(isValidPhoneNumber(phoneNumber ?? ''));
    }

    const onClickAcceptTerms = () => {
        setFormData((prevState) => ({ ...prevState, acceptedTC: !formData.acceptedTC }));
        handleBlur({ target: { name: 'acceptedTC' } } as any);
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setShowValidation(true);
        const { isValid, errors } = await validateForm(validationSchema, { validPhone, emailExists });

        if (isValid) {
            trackDataLayerEvent({ event: GAAction.REGISTRATION_SINGLE_STEP_COMPLETED, registrationStep: 'step_account' });
            setFormStep(3);
        }
    }

    return (
        <div>
            <div className='mb-1'>
                <h1 className="title-black"><FormattedMessage id="REGISTRATION_ACCOUNT_DETAILS_TITLE" /></h1>
                <p><FormattedMessage id="REGISTRATION_ACCOUNT_DETAILS_SUBTITLE" /></p>
            </div>

            <form onSubmit={onSubmit} id='form-step-account' className={`full-width-form ${showValidation ? 'show-validation-messages' : ''}`}>

                <fieldset className='registration-group'>
                    <h3 className='bold-title'>
                        <FormattedMessage id='REGISTRATION_FIELDSET_ACCOUNT_DETAILS' />
                    </h3>

                    <div className="grid-2-col-gap">
                        <div className="col">
                            <label htmlFor="regFirstName"><FormattedMessage id="REGISTRATION_FIRST_NAME" /></label>
                            <input type="text" id="regFirstName" name="firstName" aria-label='firstName'
                                className={`text-field ${getClassNames('firstName')}`}
                                placeholder={formatMessage({ id: 'PLACEHOLDER_FIRST_NAME' })}
                                autoComplete="given-name"
                                value={formData?.firstName}
                                onChange={(e) => handleUserInput(e)}
                                onBlur={handleBlur}
                            />
                            <ValidationError fieldName='firstName' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className='col'>
                            <label htmlFor="regLastName"><FormattedMessage id="REGISTRATION_LAST_NAME" /></label>
                            <input type="text" id="regLastName" name="lastName" aria-label='lastName'
                                className={`text-field ${getClassNames('lastName')}`}
                                placeholder={formatMessage({ id: 'PLACEHOLDER_LAST_NAME' })}
                                autoComplete="family-name"
                                value={formData?.lastName}
                                onChange={(e) => handleUserInput(e)}
                                onBlur={handleBlur}
                            />
                            <ValidationError fieldName='lastName' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className="col">
                            <label htmlFor="regPhoneNumber"><FormattedMessage id="REGISTRATION_PHONE_NUMBER" /></label>
                            <div className={'phone-input-container ' + (errors?.phoneNumber ? 'error' : 'valid')}>
                                <PhoneInputWithCountrySelect
                                    //data-testid='jos'
                                    id="regPhoneNumber"
                                    name="phonenumber"
                                    international
                                    defaultCountry={mapUserLanguageToCountry(userLanguage) as CountryCode}
                                    placeholder={formatMessage({ id: 'PLACEHOLDER_PHONE' })}
                                    value={formData.phoneNumber}
                                    onChange={(value) => onPhoneNumberChange(value!)}
                                    labels={getPhoneCountryLabels(userLanguage)}
                                />
                            </div>
                            <ValidationError fieldName='phoneNumber' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className='col'>
                            <label htmlFor="regEmailAddress"><FormattedMessage id="REGISTRATION_EMAIL_ADDRESS" /></label>
                            <div className='rel'>
                                <input type="text" id="regEmailAddress" name="emailAddress" aria-label='emailAddress'
                                    className={`text-field ${getClassNames('emailAddress')}`}
                                    placeholder={formatMessage({ id: 'PLACEHOLDER_EMAIL' })}
                                    autoComplete="email"
                                    value={formData?.emailAddress}
                                    onChange={(e) => handleUserInput(e)}
                                    onBlur={handleBlur}
                                />
                                {loadingEmail && (
                                    <div id="circle-spinner-vat" className="circle-spinner" />
                                )}
                            </div>
                            <ValidationError fieldName='emailAddress' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className='col'>
                            <label htmlFor="regPassword"><FormattedMessage id="REGISTRATION_PASSWORD" /></label>
                            <PasswordInput
                                password={formData.password}
                                getClassNames={getClassNames}
                                onChange={handleUserInput}
                                onBlur={handleBlur}
                            />
                            <ValidationError fieldName='password' errors={errors} showValidation={showValidation} />
                        </div>

                    </div>
                </fieldset>


                <fieldset className='registration-group'>
                    <div className='grid-2-col-gap'>
                        <div className='col'>
                            <h3 className='bold-title'>
                                <FormattedMessage id='REGISTRATION_FIELDSET_YOUR_FLEET' />
                            </h3>
                            <label htmlFor="regNumberOfCars"><FormattedMessage id="REGISTRATION_NUMBER_OF_CARS" /></label>
                            <div className='rel'>
                                <input type="number" id="regNumberOfCars" name="numCars" aria-label="numCars"
                                    disabled={flowType === 'OEM'}
                                    placeholder={flowType === 'OEM' ? '1' : '10'}
                                    min={1}
                                    max={99999}
                                    step={1}
                                    className={`text-field ${getClassNames('numCars')}`}
                                    value={formData?.numCars}
                                    onChange={(e) => handleUserInput(e)}
                                    onBlur={handleBlur}
                                />
                                {flowType === 'OEM' && (
                                    <div className='input-field-icon'>
                                        <ClickableInfoIcon modal={ModalEnum.INFO_MODAL} modalTitleFormattedMessageId='MODAL_MAX_1_CAR_TITLE' modalTextFormattedMessageId='MODAL_MAX_1_CAR_TEXT' />
                                    </div>
                                )}
                                <ValidationError fieldName='numCars' errors={errors} showValidation={showValidation} />
                            </div>
                        </div>
                    </div>
                </fieldset>


                {(flowType === 'TRACK_AND_TRACE') && (
                    <fieldset className='registration-group'>
                        <h3 className='bold-title'>
                            <FormattedMessage id='REGISTRATION_FIELDSET_TELEMATIC_PROVIDER' />
                        </h3>
                        <div className='col'>
                            <label htmlFor="telematic-provider-dropdown" className="align-left">
                                <FormattedMessage id="REGISTRATION_LABEL_TELEMATIC_PROVIDER" />
                            </label>
                            <TelematicProvidersDropdown
                                telematicProvider={formData.telematicProvider}
                                onChange={(option: DropdownOption) =>
                                    setFormData((prevState) => ({ ...prevState, telematicProvider: option.value }))}
                            />
                            <ValidationError fieldName='telematicProvider' errors={errors} showValidation={showValidation} />
                        </div>

                        {formData.telematicProvider === 'OTHER' && (
                            <div className='col'>
                                <label htmlFor="telematic-provider-dropdown" className="align-left">
                                    <FormattedMessage id="CONNECT_WITH_NAME_OF_YOUR_TELEMATIC_PROVIDER" />
                                </label>
                                <input type='text' id="regOtherTelematicProvider" name='otherTelematicProvider' aria-label='otherTelematicProvider'
                                    className={`text-field ${getClassNames('otherTelematicProvider')}`}
                                    value={formData.otherTelematicProvider}
                                    onChange={(e) => handleUserInput(e)}
                                    onBlur={handleBlur}
                                />
                                <ValidationError fieldName='otherTelematicProvider' errors={errors} showValidation={showValidation} />
                            </div>
                        )}
                    </fieldset>
                )}

                {(flowType === 'OEM') && (
                    <Fragment>
                        <div className='grid-2-col-gap'>
                            <div className='col'>
                                <label htmlFor="regLicensePlate"><FormattedMessage id="REGISTRATION_LICENSE_PLATE" /></label>
                                <div className='flex'>
                                    <ReactFlagsSelect
                                        rfsKey='rfs-licenseplate-countrycode'
                                        id='lp-countrycode-select'
                                        searchable={true}
                                        searchPlaceholder={formatMessage(placeholders.search)}
                                        selected={formData.licensePlateCountryCode!}
                                        className={`country-flags react-flags-select-inside-text-field text-field ${getClassNames('licensePlate')}`}
                                        selectButtonClassName={"react-flags-select-button"}
                                        onSelect={(countryCode) => {
                                            setFormData((prevState) => ({ ...prevState, licensePlateCountryCode: countryCode }));
                                            setTouched((prevState) => ({ ...prevState, licensePlateCountryCode: true }))
                                        }}
                                        countries={countryCodes}
                                        customLabels={getCountryCodeLabels()}
                                        selectedSize={20}
                                        optionsSize={18}
                                    />
                                    <input type="text" id="regLicensePlate" name="licensePlate" aria-label="licensePlate"
                                        className={`text-field ${getClassNames('licensePlate')}`}
                                        placeholder={formatMessage({ id: 'PLACEHOLDER_LICENSE_PLATE' })}
                                        autoComplete="license-plate"
                                        value={formData?.licensePlate}
                                        onChange={(e) => {
                                            const sanitizedLP = stripStringFromNonAlphaNumericCharacters(e.target.value).toUpperCase();
                                            setFormData((prevState) => ({ ...prevState, licensePlate: sanitizedLP }))
                                        }}
                                        onBlur={handleBlur}
                                    />
                                </div>
                                <ValidationError fieldName='licensePlate' errors={errors} showValidation={showValidation} />
                            </div>

                            <div className='col'>
                                <label htmlFor="regVIN"><FormattedMessage id="REGISTRATION_VIN" /></label>
                                <div className='rel'>
                                    <input type="text" id="regVIN" name="vin" aria-label="vin"
                                        maxLength={17}
                                        className={`text-field ${getClassNames('vin')}`}
                                        placeholder={formatMessage({ id: 'PLACEHOLDER_VIN' })}
                                        autoComplete="VIN"
                                        value={formData?.vin ?? ''}
                                        onChange={(e) => {
                                            const strippedVIN = stripStringFromNonAlphaNumericCharacters(e.target.value)?.toUpperCase();
                                            setFormData((prevState) => ({ ...prevState, vin: strippedVIN }));
                                        }}
                                        onBlur={handleBlur}
                                    />
                                    <div className='input-field-icon'>
                                        <ClickableInfoIcon modal={ModalEnum.INFO_MODAL} modalTitleFormattedMessageId='MODAL_VIN_LOCATION_TITLE' modalTextFormattedMessageId='MODAL_VIN_LOCATION_TEXT' />
                                    </div>
                                </div>
                                <ValidationError fieldName='vin' errors={errors} showValidation={showValidation} />
                            </div>
                        </div>
                    </Fragment>
                )}


                <div className='mb-1'>
                    <InputAcceptTC
                        onClickAcceptTerms={onClickAcceptTerms}
                        acceptedTC={formData?.acceptedTC!}
                        getClassNames={getClassNames}
                    />
                    <ValidationError fieldName='acceptedTC' errors={errors} showValidation={showValidation} />
                </div>

                <FormInvalidMessage errors={errors} showValidation={showValidation} />

                <div className='flex-space-between mt-1'>
                    <button type="button" className="btn-back" onClick={() => setFormStep(1)}>
                        <FontAwesomeIcon icon={faArrowLeft} size='xs' />
                        <span><FormattedMessage id="GLOBAL_CTA_BACK" /></span>
                    </button>
                    <button type="submit" className="btn-new">
                        <FormattedMessage id="GLOBAL_CTA_CONTINUE" />
                        <FontAwesomeIcon icon={faArrowRight} size='xs' />
                    </button>
                </div>

            </form >
        </div >
    );
}


type Props = {
    formData: FormRegistrationData,
    flowType: RegistrationFlowType,
    userLanguage: SupportedLocale,
    touched: TouchedFields,
    errors: FormRegistrationData,
    setTouched: Dispatch<SetStateAction<TouchedFields>>,
    setErrors: Dispatch<SetStateAction<FormRegistrationData>>,
    setFormData: Dispatch<SetStateAction<FormRegistrationData>>,
    setFormStep: Dispatch<SetStateAction<number>>,
    handleUserInput: (e: ChangeEvent<HTMLInputElement>) => void,
    handleBlur: React.FocusEventHandler<HTMLInputElement>,
    getClassNames: Function,
    validateForm: (schema: Schema<any>, context: any) => Promise<{ isValid: boolean, errors: any }>
}
